import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

const TYPES = [
  { val: 'vet', label: 'Veterinarian', icon: '🩺' },
  { val: 'farrier', label: 'Farrier', icon: '🔨' },
  { val: 'bodyworker', label: 'Bodyworker', icon: '🙌' },
  { val: 'chiropractor', label: 'Chiropractor', icon: '🦴' },
  { val: 'dentist', label: 'Dentist', icon: '🦷' },
  { val: 'saddle_fitter', label: 'Saddle fitter', icon: '🐎' },
  { val: 'trainer', label: 'Trainer', icon: '🏇' },
  { val: 'hauler', label: 'Hauler', icon: '🚚' },
  { val: 'other', label: 'Other', icon: '📋' },
];
const typeLabel = (v) => (TYPES.find(t => t.val === v) || {}).label || v;
const typeIcon = (v) => (TYPES.find(t => t.val === v) || {}).icon || '📋';

const phoneDigits = (value) => String(value || '').replace(/\D/g, '');

const formatPhoneDisplay = (value) => {
  const raw = String(value || '').trim();
  let digits = phoneDigits(raw);
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) return raw;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const normalizePhoneForTwilio = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const digits = phoneDigits(raw);
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (raw.startsWith('+') && digits.length >= 10) return `+${digits}`;
  return '';
};

const ContactModal = ({ contact, orgs, onClose, onSave }) => {
  const blank = { contact_type: 'vet', name: '', business_name: '', phone: '', email: '', notes: '', shared_org_id: '' };
  const [form, setForm] = useState(contact ? { ...contact, phone: formatPhoneDisplay(contact.phone) } : blank);
  const [saving, setSaving] = useState(false);
  const set = useCallback((f) => (e) => { const v = e.target.value; setForm(p => ({ ...p, [f]: v })); }, []);

  const handlePhoneBlur = () => {
    setForm(p => ({ ...p, phone: formatPhoneDisplay(p.phone) }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name required');

    const normalizedPhone = normalizePhoneForTwilio(form.phone);
    if (form.phone.trim() && !normalizedPhone) {
      return toast.error('Enter a 10-digit phone number, like (830) 555-1234.');
    }

    setSaving(true);
    try {
      const payload = { ...form, phone: normalizedPhone, shared_org_id: form.shared_org_id || null };
      const { data } = contact
        ? await api.put(`/contacts/${contact.id}`, payload)
        : await api.post('/contacts', payload);
      toast.success(contact ? 'Updated' : 'Contact added');
      onSave(data);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2>{contact ? 'Edit contact' : 'Add contact'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <div className="form-group">
          <label className="form-label">Type</label>
          <div className="chip-group">
            {TYPES.map(t => (
              <div key={t.val} className={`chip ${form.contact_type === t.val ? 'selected' : ''}`} onClick={() => setForm(p => ({ ...p, contact_type: t.val }))}>{t.icon} {t.label}</div>
            ))}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Name *</label>
          <input className="form-input" value={form.name} onChange={set('name')} placeholder="e.g. Dr. Sarah Mills" autoComplete="off" />
        </div>
        <div className="form-group">
          <label className="form-label">Business / practice</label>
          <input className="form-input" value={form.business_name} onChange={set('business_name')} placeholder="e.g. Brazos Valley Equine" autoComplete="off" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input
              className="form-input"
              type="tel"
              value={form.phone}
              onChange={set('phone')}
              onBlur={handlePhoneBlur}
              placeholder="e.g. (830) 555-1234"
              autoComplete="tel"
            />
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
              Saved for texting as +1XXXXXXXXXX.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email} onChange={set('email')} />
          </div>
        </div>
        {orgs.length > 0 && (
          <div className="form-group">
            <label className="form-label">Share with a barn directory (optional)</label>
            <select className="form-input" value={form.shared_org_id || ''} onChange={set('shared_org_id')}>
              <option value="">Keep private</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Notes</label>
          <input className="form-input" value={form.notes} onChange={set('notes')} autoComplete="off" />
        </div>
        <button className="btn btn-primary btn-full" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
      </div>
    </div>
  );
};

const ContactsPage = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = () => {
    Promise.all([api.get('/contacts'), api.get('/organizations/mine')])
      .then(([c, o]) => { setContacts(c.data); setOrgs(o.data.filter(x => x.my_status === 'active')); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try { await api.delete(`/contacts/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const filtered = filter === 'all' ? contacts : contacts.filter(c => c.contact_type === filter);
  const usedTypes = [...new Set(contacts.map(c => c.contact_type))];

  return (
    <div>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ fontSize: 18 }}>Care team</h1>
            <p style={{ opacity: 0.85, fontSize: 12, marginTop: 2 }}>Vets, farriers & providers</p>
          </div>
          <button onClick={() => setModal({})} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 13, borderRadius: 8, padding: '6px 14px', fontWeight: 500 }}>+ Add</button>
        </div>
      </div>

      <div className="page-content">
        {usedTypes.length > 1 && (
          <div className="chip-group" style={{ marginBottom: 12 }}>
            <div className={`chip ${filter === 'all' ? 'selected' : ''}`} onClick={() => setFilter('all')}>All</div>
            {usedTypes.map(t => (
              <div key={t} className={`chip ${filter === t ? 'selected' : ''}`} onClick={() => setFilter(t)}>{typeIcon(t)} {typeLabel(t)}</div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="loading-screen"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🩺</div>
            <h3>No contacts yet</h3>
            <p>Add your vet, farrier, and other providers once — then attach them to any horse.</p>
          </div>
        ) : (
          filtered.map(c => (
            <div key={c.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="icon-box" style={{ background: 'var(--cr-primary-light)', fontSize: 18 }}>{typeIcon(c.contact_type)}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {typeLabel(c.contact_type)}{c.business_name ? ` · ${c.business_name}` : ''}
                </div>
                {c.phone && <div style={{ fontSize: 12, color: 'var(--cr-primary)', marginTop: 2 }}>{formatPhoneDisplay(c.phone)}</div>}
                {c.shared_org_id && <span className="badge badge-green" style={{ marginTop: 4 }}>Shared with barn</span>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setModal(c)}>Edit</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c.id)}>Delete</button>
              </div>
            </div>
          ))
        )}
      </div>

      {modal && <ContactModal contact={modal.id ? modal : null} orgs={orgs} onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }} />}
    </div>
  );
};

export default ContactsPage;
